/* =============================================================================
   careers.js  -  Paris Pharmacy careers (detail pages).
   Drives: in-page EN|FR toggle, reveal-on-scroll, 4 step form, file picker,
   client validation, and the cross-origin 3 phase write to the careers-api.
   HARD RULE: zero em dashes (U+2014) and zero en dashes (U+2013) anywhere.

   Network contract (derived from build/careers-api/api/*.js + lib/shared.js):
     Phase 1  POST {base}/api/signed-url
              body: { filename, contentType, position, lang, hp, t }
              200 -> { applicationId, path, token, signedUrl }
     Phase 2  PUT  {signedUrl}  (raw file body, header Content-Type = file type)
     Phase 3  POST {base}/api/submit
              body: { applicationId, position, lang, name, email, phone, links,
                      answers:{ q1,q2,q3,observation, start_date,convention,
                        gratification_ok,location_ok,absences,autonomy,
                        ops_reality,ops_reality_note,excel_level }, consent, hp, t }
              (ops_reality and excel_level are acheteur only; server allowlists keys)
              200 -> { ok: true }   (cvPath is NOT sent; the server owns it)
   Status codes handled: 403 forbidden, 422 validation/consent, 429 rate limit,
   5xx generic retry. Honeypot field `hp` must stay empty. `t` = ms since render.
   ============================================================================= */
(function () {
  'use strict';

  // ---- config: the careers-api base URL is injected by the Liquid section ----
  // via window.CAREERS_API_BASE so it is changeable from the theme editor
  // without editing this file.
  var API_BASE = (window.CAREERS_API_BASE || 'https://careers-api-neyro.vercel.app')
    .replace(/\/+$/, '');

  // ---- client side mirror of the server rules (server stays authoritative) ----
  var ALLOWED_EXT = ['pdf', 'doc', 'docx'];
  var CONTENT_TYPE_BY_EXT = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  };
  var MAX_BYTES = 8 * 1024 * 1024; // 8 MB
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var MIN_ELAPSED_MS = 2000; // server rejects faster than this

  // ---- bilingual UI strings used only by JS (page copy lives in Liquid) -----
  var MSG = {
    en: {
      sending: 'Sending your application...',
      uploading: 'Uploading your CV...',
      need_name: 'Please add your full name.',
      need_email: 'Please enter a valid email.',
      need_phone: 'Please add a phone number.',
      need_q1: 'Please answer the first question.',
      need_start: 'Please add a start date.',
      need_choice: 'Please pick an option to continue.',
      need_cv: 'Please attach your CV.',
      bad_ext: 'CV must be a PDF, DOC or DOCX file.',
      too_big: 'Your CV is over 8 MB. Please send a smaller file.',
      need_consent: 'Please tick the consent box to continue.',
      err_403: 'We could not accept this request from here. Please reload the page and try again.',
      err_422: 'Some details look off. Please review your answers and try again.',
      err_429: 'Too many attempts. Please try again a little later.',
      err_5xx: 'Something went wrong on our side. Please try again in a moment.',
      err_net: 'Network issue. Please check your connection and try again.',
      err_upload: 'We could not upload your CV. Please try again.'
    },
    fr: {
      sending: 'Envoi de ta candidature...',
      uploading: 'Envoi de ton CV...',
      need_name: 'Ajoute ton nom complet.',
      need_email: 'Saisis un email valide.',
      need_phone: 'Ajoute un numéro de téléphone.',
      need_q1: 'Réponds à la première question.',
      need_start: 'Indique une date de démarrage.',
      need_choice: 'Choisis une option pour continuer.',
      need_cv: 'Joins ton CV.',
      bad_ext: 'Le CV doit être un fichier PDF, DOC ou DOCX.',
      too_big: 'Ton CV dépasse 8 Mo. Envoie un fichier plus léger.',
      need_consent: 'Coche la case de consentement pour continuer.',
      err_403: 'Nous ne pouvons pas accepter cette demande ici. Recharge la page et réessaie.',
      err_422: 'Certaines informations semblent incorrectes. Vérifie tes réponses et réessaie.',
      err_429: 'Trop de tentatives. Réessaie un peu plus tard.',
      err_5xx: 'Un problème est survenu de notre côté. Réessaie dans un instant.',
      err_net: 'Problème réseau. Vérifie ta connexion et réessaie.',
      err_upload: 'Nous n’avons pas pu envoyer ton CV. Réessaie.'
    }
  };

  function extOf(name) {
    var m = /\.([a-z0-9]+)$/i.exec(name || '');
    return m ? m[1].toLowerCase() : '';
  }

  // ===========================================================================
  // submitApplication : THE single entry point for all careers-api traffic.
  // ===========================================================================
  // payload = { position, lang, name, email, phone, links, answers, consent, hp, t }
  //   answers = { q1, q2, q3, observation }
  // file = the chosen CV File object.
  // onProgress(stageKey) is called with 'uploading' then 'sending' so the UI
  // can show a friendly bilingual status.
  //
  // Resolves { ok: true } on success.
  // Rejects with an Error carrying { code } where code is one of:
  //   'forbidden'(403) 'validation'(422) 'rate_limited'(429) 'server'(5xx)
  //   'network' 'upload'. The caller maps code -> a bilingual message.
  async function submitApplication(payload, file, onProgress) {
    var lang = payload.lang === 'fr' ? 'fr' : 'en';
    var progress = typeof onProgress === 'function' ? onProgress : function () {};

    // ---- phase 1: request the signed upload URL + create the pending row ----
    var ext = extOf(file && file.name);
    // Prefer the browser-reported type; fall back to the extension map so a
    // missing file.type (some OSes report '' for .doc) still maps correctly.
    var contentType = (file && file.type) || CONTENT_TYPE_BY_EXT[ext] || '';

    var signRes;
    try {
      signRes = await fetch(API_BASE + '/api/signed-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file ? file.name : '',
          contentType: contentType,
          position: payload.position,
          lang: lang,
          hp: payload.hp || '',
          t: payload.t
        })
      });
    } catch (e) {
      throw withCode(new Error('network'), 'network');
    }
    if (!signRes.ok) throw httpError(signRes.status);

    var signed;
    try {
      signed = await signRes.json();
    } catch (e) {
      throw withCode(new Error('server'), 'server');
    }
    if (!signed || !signed.signedUrl || !signed.applicationId) {
      throw withCode(new Error('server'), 'server');
    }

    // ---- phase 2: PUT the raw file straight to Supabase Storage --------------
    progress('uploading');
    var putRes;
    try {
      putRes = await fetch(signed.signedUrl, {
        method: 'PUT',
        // The upload PUT Content-Type MUST be the real file type so Storage
        // stores the object with the right MIME (the bucket allow-lists these).
        headers: { 'Content-Type': contentType || 'application/octet-stream' },
        body: file
      });
    } catch (e) {
      throw withCode(new Error('upload'), 'upload');
    }
    if (!putRes.ok) throw withCode(new Error('upload'), 'upload');

    // ---- phase 3: finalize the application -----------------------------------
    progress('sending');
    var subRes;
    try {
      subRes = await fetch(API_BASE + '/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicationId: signed.applicationId,
          position: payload.position,
          lang: lang,
          name: payload.name,
          email: payload.email,
          phone: payload.phone,
          links: payload.links,
          // Forward the whole answers object; the server allowlists keys and
          // drops anything it does not recognize, so this stays future proof.
          answers: payload.answers,
          consent: payload.consent === true,
          hp: payload.hp || '',
          t: payload.t
        })
      });
    } catch (e) {
      throw withCode(new Error('network'), 'network');
    }
    if (!subRes.ok) throw httpError(subRes.status);

    var out;
    try {
      out = await subRes.json();
    } catch (e) {
      throw withCode(new Error('server'), 'server');
    }
    if (!out || out.ok !== true) throw withCode(new Error('server'), 'server');
    return out;
  }

  function withCode(err, code) { err.code = code; return err; }
  function httpError(status) {
    if (status === 403) return withCode(new Error('forbidden'), 'forbidden');
    if (status === 422) return withCode(new Error('validation'), 'validation');
    if (status === 429) return withCode(new Error('rate_limited'), 'rate_limited');
    if (status >= 500) return withCode(new Error('server'), 'server');
    return withCode(new Error('http_' + status), 'server');
  }

  function messageForCode(code, lang) {
    var m = MSG[lang] || MSG.en;
    switch (code) {
      case 'forbidden': return m.err_403;
      case 'validation': return m.err_422;
      case 'rate_limited': return m.err_429;
      case 'upload': return m.err_upload;
      case 'network': return m.err_net;
      default: return m.err_5xx;
    }
  }

  // Expose for testing / reuse if ever needed elsewhere.
  window.submitApplication = submitApplication;

  // ===========================================================================
  // UI wiring
  // ===========================================================================
  document.addEventListener('DOMContentLoaded', function () {
    var root = document.querySelector('.cr-root');
    if (root) root.classList.add('js');

    // reveal on scroll
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
        });
      }, { threshold: 0.12 });
      document.querySelectorAll('.cr-root .reveal').forEach(function (el) { io.observe(el); });
    } else {
      document.querySelectorAll('.cr-root .reveal').forEach(function (el) { el.classList.add('in'); });
    }

    // in-page EN | FR toggle (does NOT depend on Shopify locale routing).
    // v1.1 SEO item: add proper /fr/ locale URLs + <link rel="alternate" hreflang>
    // for real per-language indexable URLs. Not built here on purpose.
    function setLang(l) {
      document.body.classList.toggle('cr-fr', l === 'fr');
      document.querySelectorAll('.cr-root .langsw button').forEach(function (b) {
        var on = b.getAttribute('data-l') === l;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    document.querySelectorAll('.cr-root .langsw button').forEach(function (b) {
      b.addEventListener('click', function () { setLang(b.getAttribute('data-l')); });
    });

    // each detail page embeds one EN form and one FR form; wire both.
    document.querySelectorAll('.cr-root form[data-form]').forEach(wireForm);
  });

  function wireForm(form) {
    var lang = form.getAttribute('data-lang') === 'fr' ? 'fr' : 'en';
    var position = form.getAttribute('data-position');
    var msg = MSG[lang] || MSG.en;

    var steps = Array.prototype.slice.call(form.querySelectorAll('.fstep'));
    var bars = Array.prototype.slice.call(form.querySelectorAll('.progress i'));
    var stepsWrap = form.querySelector('.fsteps');
    var renderedAt = Date.now(); // timing trap baseline (t = now - renderedAt)
    var idx = 0;
    var submitting = false;
    var animating = false;
    // Lead capture (abandoned step 1): once step 1 validates we snapshot the
    // contact details; if the visitor then LEAVES the page without finishing,
    // a sendBeacon ships them to /api/lead so the founder can re-engage.
    var leadData = null;
    var leadSent = false;
    var submittedOk = false;

    function prefersReduced() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    // Move focus into the revealed step so keyboard / screen-reader users keep
    // their place (the control they clicked is about to be display:none, which
    // would otherwise drop focus back to the document top).
    function focusStep(s) {
      var h = s.querySelector('h3') || s.querySelector('[name], button');
      if (h) {
        h.setAttribute('tabindex', '-1');
        try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
      }
    }
    // Only pull the form back into view if its top has scrolled ABOVE the
    // viewport. An in-view Next/Back must never yank the page (that jump was the
    // main cause of the old "not smooth" feel).
    function maybeScroll() {
      var rect = (stepsWrap || form).getBoundingClientRect();
      if (rect.top < 0) {
        form.scrollIntoView({ behavior: prefersReduced() ? 'auto' : 'smooth', block: 'start' });
      }
    }

    // Smooth step change: crossfade the outgoing and incoming steps while the
    // wrapper animates its height between the two, so there is no height snap.
    function show(n) {
      if (n === idx || animating) return;
      var prev = steps[idx];
      var next = steps[n];
      bars.forEach(function (b, k) { b.classList.toggle('on', k <= n); });

      if (prefersReduced() || !stepsWrap) {
        prev.classList.remove('on');
        next.classList.add('on');
        idx = n;
        focusStep(next);
        maybeScroll();
        return;
      }

      animating = true;
      var startH = stepsWrap.offsetHeight;
      stepsWrap.style.height = startH + 'px'; // lock current height first
      void stepsWrap.offsetHeight;            // commit the lock (force reflow)
      next.classList.add('on', 'cr-enter');   // bring next into flow + fade/slide in
      prev.classList.add('cr-leave');         // take prev out of flow + fade out
      var endH = next.offsetHeight;           // natural height of the new step
      requestAnimationFrame(function () { stepsWrap.style.height = endH + 'px'; });

      function cleanup() {
        stepsWrap.removeEventListener('transitionend', onEnd);
        clearTimeout(safety);
        stepsWrap.style.height = '';
        prev.classList.remove('on', 'cr-leave');
        next.classList.remove('cr-enter');
        animating = false;
        focusStep(next);
        maybeScroll();
      }
      function onEnd(e) { if (e.target === stepsWrap && e.propertyName === 'height') cleanup(); }
      stepsWrap.addEventListener('transitionend', onEnd);
      var safety = setTimeout(cleanup, 600);
      idx = n;
    }

    var fileInput = form.querySelector('.cv');
    var drop = form.querySelector('.drop');
    var statusEl = form.querySelector('.formstatus');

    // file picker + drag and drop
    if (drop && fileInput) {
      drop.addEventListener('click', function () { fileInput.click(); });
      drop.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
      });
      fileInput.addEventListener('change', function () {
        if (fileInput.files && fileInput.files[0]) {
          var fn = drop.querySelector('.fn');
          fn.textContent = '✓ ' + fileInput.files[0].name;
          fn.style.display = 'block';
        }
      });
      ['dragover', 'dragleave', 'drop'].forEach(function (ev) {
        drop.addEventListener(ev, function (e) {
          e.preventDefault();
          if (ev === 'drop' && e.dataTransfer.files[0]) {
            fileInput.files = e.dataTransfer.files;
            fileInput.dispatchEvent(new Event('change'));
          }
        });
      });
    }

    function setStatus(text, busy) {
      if (!statusEl) return;
      statusEl.textContent = text || '';
      statusEl.classList.toggle('busy', !!busy);
    }

    // step 1 client validation before advancing
    function validateStep1() {
      var name = form.querySelector('[name="name"]');
      var email = form.querySelector('[name="email"]');
      var phone = form.querySelector('[name="phone"]');
      if (!name.value.trim()) return fail(name, msg.need_name);
      if (!EMAIL_RE.test(email.value.trim())) return fail(email, msg.need_email);
      if (!phone.value.trim()) return fail(phone, msg.need_phone);
      var f = fileInput && fileInput.files && fileInput.files[0];
      if (!f) { setStatus(msg.need_cv); return false; }
      if (ALLOWED_EXT.indexOf(extOf(f.name)) === -1) { setStatus(msg.bad_ext); return false; }
      if (f.size > MAX_BYTES) { setStatus(msg.too_big); return false; }
      setStatus('');
      return true;
    }

    // read the checked radio's value for a group, '' if none selected.
    function radioVal(name) {
      var el = form.querySelector('[name="' + name + '"]:checked');
      return el ? el.value : '';
    }
    function failRadio(name, text) {
      var group = form.querySelector('[name="' + name + '"]');
      var wrap = group ? group.closest('.radio-field') : null;
      if (wrap) wrap.classList.add('invalid');
      if (group) group.focus();
      setStatus(text);
      return false;
    }
    // Step 2 (practical fit): start date + the required screening radios. The
    // acheteur only "réalité terrain" consent is required only when present.
    function validateCadre() {
      var start = form.querySelector('[name="start_date"]');
      if (start && !start.value.trim()) return fail(start, msg.need_start);
      var req = ['convention', 'gratification_ok', 'location_ok', 'autonomy'];
      if (position === 'acheteur') req.push('ops_reality');
      for (var i = 0; i < req.length; i++) {
        if (!radioVal(req[i])) return failRadio(req[i], msg.need_choice);
      }
      setStatus('');
      return true;
    }
    // Step 3 (questions): q1 required; acheteur must also pick an Excel level.
    function validateQuestions() {
      var q1 = form.querySelector('[name="q1"]');
      if (q1 && !q1.value.trim()) return fail(q1, msg.need_q1);
      if (position === 'acheteur' && !radioVal('excel_level')) {
        return failRadio('excel_level', msg.need_choice);
      }
      setStatus('');
      return true;
    }

    function fail(el, text) {
      if (el) { el.setAttribute('aria-invalid', 'true'); el.focus(); }
      setStatus(text);
      return false;
    }
    function clearInvalid(el) { if (el) el.removeAttribute('aria-invalid'); }
    form.querySelectorAll('input,textarea').forEach(function (el) {
      el.addEventListener('input', function () { clearInvalid(el); });
    });
    // radios: clear the group's invalid ring as soon as a choice is made.
    form.querySelectorAll('input[type="radio"]').forEach(function (r) {
      r.addEventListener('change', function () {
        var wrap = r.closest('.radio-field');
        if (wrap) wrap.classList.remove('invalid');
        setStatus('');
      });
    });

    // Snapshot the step-1 contact details (no CV, no answers: those come at
    // final submit). Refreshed on every step-1 advance so it holds the latest.
    function captureLead() {
      var hp = form.querySelector('.cr-hp input');
      leadData = {
        position: position,
        lang: lang,
        name: form.querySelector('[name="name"]').value.trim(),
        email: form.querySelector('[name="email"]').value.trim(),
        phone: form.querySelector('[name="phone"]').value.trim(),
        links: (form.querySelector('[name="links"]').value || '').trim(),
        hp: hp ? hp.value : '',
        t: Date.now() - renderedAt
      };
    }
    // Fire once, only if step 1 was completed and the form was NOT submitted.
    // text/plain keeps the beacon a CORS-simple request (no preflight).
    function sendLeadBeacon() {
      if (!leadData || submittedOk || leadSent) return;
      if (!navigator.sendBeacon) return;
      leadSent = true;
      try {
        var blob = new Blob([JSON.stringify(leadData)], { type: 'text/plain;charset=UTF-8' });
        navigator.sendBeacon(API_BASE + '/api/lead', blob);
      } catch (e) { /* best effort */ }
    }
    window.addEventListener('pagehide', sendLeadBeacon);

    form.addEventListener('click', function (e) {
      var t = e.target;
      if (t.hasAttribute && t.hasAttribute('data-next')) {
        e.preventDefault();
        if (idx === 0) { if (!validateStep1()) return; captureLead(); }
        else if (idx === 1) { if (!validateCadre()) return; }
        else if (idx === 2) { if (!validateQuestions()) return; }
        if (idx < steps.length - 1) show(idx + 1);
      }
      if (t.hasAttribute && t.hasAttribute('data-prev')) {
        e.preventDefault();
        if (idx > 0) show(idx - 1);
      }
    });

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (submitting) return;

      var consent = form.querySelector('.cons');
      if (!consent || !consent.checked) { setStatus(msg.need_consent); consent && consent.focus(); return; }
      if (!validateStep1() || !validateCadre() || !validateQuestions()) return;

      var file = fileInput.files[0];
      var hp = form.querySelector('.cr-hp input');
      var payload = {
        position: position,
        lang: lang,
        name: form.querySelector('[name="name"]').value.trim(),
        email: form.querySelector('[name="email"]').value.trim(),
        phone: form.querySelector('[name="phone"]').value.trim(),
        links: (form.querySelector('[name="links"]').value || '').trim(),
        answers: {
          q1: val(form, 'q1'),
          q2: val(form, 'q2'),
          q3: val(form, 'q3'),
          observation: val(form, 'observation'),
          start_date: val(form, 'start_date'),
          convention: radioVal('convention'),
          gratification_ok: radioVal('gratification_ok'),
          location_ok: radioVal('location_ok'),
          absences: val(form, 'absences'),
          autonomy: radioVal('autonomy'),
          ops_reality: radioVal('ops_reality'),
          ops_reality_note: val(form, 'ops_reality_note'),
          excel_level: radioVal('excel_level')
        },
        consent: true,
        hp: hp ? hp.value : '',
        t: Date.now() - renderedAt // ms since this form rendered
      };

      submitting = true;
      var sendBtn = form.querySelector('[type="submit"]');
      if (sendBtn) sendBtn.disabled = true;

      try {
        await submitApplication(payload, file, function (stage) {
          setStatus(stage === 'uploading' ? msg.uploading : msg.sending, true);
        });
        submittedOk = true; // finished -> the abandon beacon must never fire
        // success: swap form body for the thank-you panel
        var body = form.querySelector('.formbody');
        if (body) body.style.display = 'none';
        var ok = form.querySelector('.success');
        if (ok) ok.classList.add('on');
        setStatus('');
      } catch (err) {
        setStatus(messageForCode(err && err.code, lang));
        submitting = false;
        if (sendBtn) sendBtn.disabled = false;
      }
    });
  }

  function val(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el ? el.value.trim() : '';
  }
})();
